import React from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import { Add as PlusIcon, Remove as MinusIcon } from '@mui/icons-material';
import { connect } from 'react-redux';

interface RoomDetail {
  name: string;
  description: string;
  pricePerNight: number;
  maxGuests: number;
  totalRooms: number;
  amenities: string[];
  facilities: string[];
  imageUrl: string;
}

interface RoomListProps {
  selectedRooms: { categoryId: string; quantity: number }[];
  onRoomSelection: (rooms: { categoryId: string; quantity: number }[]) => void;
  roomDetails: RoomDetail[];
}

const RoomList: React.FC<RoomListProps> = ({
  selectedRooms,
  onRoomSelection,
  roomDetails,
}) => {
  const handleQuantityChange = (roomName: string, quantity: number) => {
    const room = roomDetails.find((rd) => rd.name === roomName);
    if (!room) return;

    // Ensure quantity doesn't exceed total rooms
    const validQuantity = Math.min(Math.max(0, quantity), room.totalRooms);

    const newSelection = selectedRooms.filter(
      (room) => room.categoryId !== roomName
    );
    if (validQuantity > 0) {
      newSelection.push({ categoryId: roomName, quantity: validQuantity });
    }
    onRoomSelection(newSelection);
  };

  const getQuantity = (roomName: string): number => {
    return selectedRooms.find((room) => room.categoryId === roomName)?.quantity || 0;
  };

  return (
    <Grid container spacing={3}>
      {roomDetails.map((room) => {
        const currentQuantity = getQuantity(room.name);
        
        return (
          <Grid item xs={12} sm={6} md={4} key={room.name}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={room.imageUrl}
                alt={room.name}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {room.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {room.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {room.amenities.slice(0, 3).map((amenity) => (
                    <Chip 
                      key={amenity} 
                      label={amenity} 
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  {room.amenities.length > 3 && (
                    <Chip 
                      label={`+${room.amenities.length - 3} more`} 
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  )}
                </Box>
                <Typography variant="h6" color="primary">
                  ₹{room.pricePerNight.toLocaleString()} / night
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max Guests: {room.maxGuests} | Available Rooms: {room.totalRooms}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleQuantityChange(room.name, currentQuantity - 1)}
                    disabled={currentQuantity === 0}
                    color="primary"
                  >
                    <MinusIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="body1"
                    sx={{
                      minWidth: '40px',
                      textAlign: 'center',
                      userSelect: 'none',
                    }}
                  >
                    {currentQuantity}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleQuantityChange(room.name, currentQuantity + 1)}
                    disabled={currentQuantity >= room.totalRooms}
                    color="primary"
                  >
                    <PlusIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const mapStateToProps = (state: any) => {
  return {
    roomDetails: state.common?.selectedRestaurant?.roomDetails || []
  };
};

export default connect(mapStateToProps)(RoomList);
